import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { sanityImageUrl } from '../../../lib/sanityImage';
// NOTE: sanityService is imported DYNAMICALLY inside the effect below (not at
// module top) so its content-fetch code stays off the homepage's critical
// first-load path. Sanity is the CANONICAL (and only) blog source — the former
// Supabase blog fallback was removed as part of the Supabase -> Cloud SQL
// migration (the blog is NOT migrated; Sanity owns it). If Sanity returns
// nothing, this section renders nothing.

const EASE_OUT_EXPO = [0.16, 1, 0.3, 1];

// Scroll-in motion variants — section-level container propagates
// hidden/visible state to the header + cards via initial="hidden"
// whileInView="visible" on the wrapping motion.section.
const sectionV = { hidden: {}, visible: {} };

const eyebrowV = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: EASE_OUT_EXPO } },
};

const headlineV = {
  hidden: { opacity: 0, y: 16 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.55, delay: 0.08, ease: EASE_OUT_EXPO } },
};

// Each card cascades after the header, staggered 80ms per index.
const cardV = {
  hidden: { opacity: 0, y: 24 },
  visible: (i) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, delay: 0.25 + i * 0.08, ease: EASE_OUT_EXPO },
  }),
};

// "See All Posts" CTA — lands after the 3 cards have settled.
const ctaV = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: 0.60, ease: EASE_OUT_EXPO } },
};

const SanityContentSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPosts = async () => {
      try {
        // Lazily pull the Sanity content service only when this section loads.
        const { fetchSanityContent } = await import('../../../services/sanityService');
        const sanityData = await fetchSanityContent('post');
        if (sanityData?.length) {
          // Normalize Sanity posts - sort by date and limit to 3
          const normalized = sanityData
            ?.map((item) => ({
              id: item?._id,
              title: item?.title,
              excerpt: item?.excerpt || item?.description,
              // Cards render <=~400px CSS wide (3-col grid / mobile full
              // width); w=800 covers 2x retina. Without this transform the
              // CDN serves the 2048-2816px originals (~2.2MB for 3 cards).
              imageUrl: sanityImageUrl(item?.mainImageUrl || item?.imageUrl || null, 800),
              slug: item?.slug,
              publishedAt: item?.publishedAt || item?._createdAt,
              source: 'sanity',
            }))
            ?.sort((a, b) => new Date(b?.publishedAt) - new Date(a?.publishedAt))
            ?.slice(0, 3);
          setItems(normalized);
        } else {
          // No Sanity posts → render nothing (Supabase blog fallback removed).
          setItems([]);
        }
      } catch (err) {
        console.error('Failed to load blog posts:', err);
      } finally {
        setLoading(false);
      }
    };

    loadPosts();
  }, []);

  if (loading) {
    return (
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex justify-center items-center gap-2 text-gray-400">
            <div className="w-5 h-5 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin" />
            <span className="text-sm">Loading content…</span>
          </div>
        </div>
      </section>
    );
  }

  if (!items?.length) {
    return null;
  }

  return (
    <motion.section
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
      variants={sectionV}
      className="py-16 px-4 bg-white"
    >
      <div className="max-w-5xl mx-auto">
        {/* Header — eyebrow + headline cascade into view on scroll. */}
        <motion.div
          variants={eyebrowV}
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-5"
          style={{ backgroundColor: 'rgba(7,176,233,0.10)', color: '#07B0E9', border: '1px solid rgba(7,176,233,0.25)' }}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-current animate-pulse" />
          From Our Blog
        </motion.div>
        <motion.h2
          variants={headlineV}
          className="text-3xl sm:text-4xl font-black mb-10"
          style={{ color: '#000150', fontFamily: "'Poppins', 'Montserrat', sans-serif", letterSpacing: '-0.02em' }}
        >
          Latest Updates
        </motion.h2>

        {/* Cards — staggered fade-up via custom index. Existing hover
            translate / image scale / title color transitions preserved
            on the inner Link (CSS transitions, not motion variants). */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items?.map((item, i) => (
            <motion.div
              key={item?.id}
              variants={cardV}
              custom={i}
            >
              <Link
                to={item?.slug ? `/blog/${item?.slug}` : '/blog'}
                className="rounded-2xl border border-gray-100 overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 hover:-translate-y-1 bg-white block group h-full"
              >
                {item?.imageUrl ? (
                  <div className="relative w-full h-44 overflow-hidden">
                    <img
                      src={item?.imageUrl}
                      alt={item?.title || 'Blog post image'}
                      className="w-full h-44 object-cover group-hover:scale-110 transition-transform duration-500 ease-out"
                    />
                    {/* Subtle cyan gradient overlay on hover — emerges
                        from the bottom of the image. Brand reinforcement. */}
                    <div
                      className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
                      style={{ background: 'linear-gradient(to top, rgba(7, 176, 233, 0.18), transparent 50%)' }}
                    />
                  </div>
                ) : (
                  <div
                    className="w-full h-44 flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #000150 0%, #07B0E9 100%)' }}
                  >
                    <span className="text-white text-opacity-60 text-4xl font-black opacity-20">
                      OQ
                    </span>
                  </div>
                )}
                <div className="p-5">
                  {item?.title && (
                    <h3
                      className="text-base font-bold mb-2 line-clamp-2 group-hover:text-[#07B0E9] transition-colors"
                      style={{ color: '#000150' }}
                    >
                      {item?.title}
                    </h3>
                  )}
                  {item?.excerpt && (
                    <p className="text-gray-500 text-sm line-clamp-3">{item?.excerpt}</p>
                  )}
                  {item?.publishedAt && (
                    <p className="text-xs text-gray-400 mt-3">
                      {new Date(item.publishedAt)?.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </p>
                  )}
                  <span className="inline-flex items-center gap-1 mt-3 text-xs font-semibold group-hover:gap-2 transition-all" style={{ color: '#07B0E9' }}>
                    Read more
                    <span className="transition-transform duration-200 group-hover:translate-x-1">→</span>
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* CTA — lands after the cards. Hover lifts + intensifies shadow. */}
        <motion.div variants={ctaV} className="flex justify-center mt-10">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 px-7 py-3 rounded-full font-bold text-sm transition-all duration-200 hover:-translate-y-0.5"
            style={{
              backgroundColor: '#07B0E9',
              color: '#fff',
              boxShadow: '0 8px 24px rgba(7, 176, 233, 0.4)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '0 12px 32px rgba(7, 176, 233, 0.6)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(7, 176, 233, 0.4)';
            }}
          >
            See All Posts
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </Link>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default SanityContentSection;
